import { useCallback, useRef } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import httpInstances from '@/api/axiosInstance';
import { getDashboardApiUrl } from '@/features/Dashboard/api/dashboardUrl';
import { dashboardQueryKeys } from '@/features/Dashboard/api/queries';
import { TAB_DEFAULT_BASE_NAME } from '@/features/Dashboard/constants/names';
import { useWidgetAnimation } from '@/features/Dashboard/context/WidgetAnimation/hooks';
import { getTabContent } from '@/features/Dashboard/lib/transformers';
import { DashboardTargetMenu } from '@/features/Dashboard/pages/Edit/components/menus/DashboardTargetMenu';
import type { DashboardTab, MarketApps } from '@/features/Dashboard/types';
import { getUniqueDashboardName } from '@/features/Dashboard/utils/getUniqueDashboardName';
import { getUniqueTabName } from '@/features/Dashboard/utils/getUniqueTabName';
import { useDashboard } from '@/hooks/api/dashboard';
import useSnackbarNotifications from '@/hooks/useSnackbarNotifications';
import WidgetAdapter from '@/pages/Markets/utils/WidgetAdapter';
import type { BaseTabType } from '@/types/markets';

interface AddToDashboardMenuProps {
  tab: BaseTabType;
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
}

const ADD_DASHBOARD_APP_SNACKBAR_KEY = 'add-dashboard-app';

const AddToDashboardMenu = ({ tab, anchorEl, open, onClose }: AddToDashboardMenuProps) => {
  const { data: allDashboards } = useDashboard();
  const { setElementsToAnimateId } = useWidgetAnimation();
  const { showSuccessSnackbar, showErrorSnackbar, showLoadingSnackbar } =
    useSnackbarNotifications();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const navigationTargetRef = useRef<{
    dashboardName: string;
    tabName: string;
    widgetId: string;
  } | null>(null);

  // Generic mutation for adding widgets to any dashboard/tab
  const { mutate: addWidgetToDashboard, isPending } = useMutation({
    mutationFn: async ({
      dashboardName,
      tabName,
      widgetId,
      payload
    }: {
      dashboardName: string;
      tabName: string;
      widgetId: string;
      payload: DashboardTab;
    }) => {
      // Store navigation target and widget ID for onSuccess
      navigationTargetRef.current = { dashboardName, tabName, widgetId };

      const response = await httpInstances.axiosInternalInstance.post(
        getDashboardApiUrl(encodeURIComponent(dashboardName)),
        payload
      );
      await new Promise<void>(resolve => {
        setTimeout(() => {
          resolve();
        }, 2000);
      });
      return response;
    },
    onMutate: () => {
      showLoadingSnackbar('Adding app to dashboard...', ADD_DASHBOARD_APP_SNACKBAR_KEY);
    },
    onSuccess: async () => {
      showSuccessSnackbar('App added to dashboard!', ADD_DASHBOARD_APP_SNACKBAR_KEY);

      // Wait for the dashboard query to be refetched before navigating
      await queryClient.refetchQueries({ queryKey: dashboardQueryKeys.all });

      // Set widget animation and navigate to the dashboard and tab
      if (navigationTargetRef.current) {
        const { dashboardName, tabName, widgetId } = navigationTargetRef.current;
        setElementsToAnimateId([widgetId]);
        navigate(`/dashboard/${encodeURIComponent(dashboardName)}/${encodeURIComponent(tabName)}`);
        navigationTargetRef.current = null;
      }
    },
    onError: () => {
      showErrorSnackbar(
        "Couldn't add app to dashboard. Please try again.",
        undefined,
        ADD_DASHBOARD_APP_SNACKBAR_KEY
      );
      navigationTargetRef.current = null;
    }
  });

  const createWidget = useCallback(() => {
    let name = tab.name;
    if (!name && tab.type === 'feed') {
      name = 'Feed';
    }
    return WidgetAdapter.newWidget({ ...tab, name });
  }, [tab]);

  const handleAddToTab = useCallback(
    (dashboardName: string, tabName: string) => {
      if (isPending || !allDashboards) return;

      const widget = createWidget();
      if (!widget) return;

      const existingTabs = allDashboards[dashboardName] || {};
      const existingTabValue = existingTabs[tabName];
      const existingApps = existingTabValue ? getTabContent(existingTabValue) : [];
      const updatedApps = [...existingApps, widget] as MarketApps;

      // Build the full dashboard structure to preserve all tabs
      const payload: DashboardTab = {
        ...existingTabs,
        [tabName]: updatedApps
      };

      addWidgetToDashboard({
        dashboardName,
        tabName,
        widgetId: widget.id || '',
        payload
      });
      onClose();
    },
    [isPending, createWidget, addWidgetToDashboard, onClose, allDashboards]
  );

  const handleCreateNewDashboard = useCallback(() => {
    if (isPending) return;

    const widget = createWidget();
    if (!widget) return;

    // Generate unique dashboard name
    const existingNames = Object.keys(allDashboards || {});
    const newName = getUniqueDashboardName(existingNames);

    // Create new dashboard with TAB_DEFAULT_BASE_NAME tab containing the widget
    const payload: DashboardTab = {
      [TAB_DEFAULT_BASE_NAME]: [widget] as MarketApps
    };

    addWidgetToDashboard({
      dashboardName: newName,
      tabName: TAB_DEFAULT_BASE_NAME,
      widgetId: widget.id || '',
      payload
    });
    onClose();
  }, [isPending, createWidget, allDashboards, addWidgetToDashboard, onClose]);

  const handleCreateNewTab = useCallback(
    (dashboardName: string) => {
      if (isPending || !allDashboards) return;

      const widget = createWidget();
      if (!widget) return;

      const existingTabs = allDashboards[dashboardName] || {};

      // Generate unique tab name
      const existingTabNames = Object.keys(existingTabs);
      const newTabName = getUniqueTabName(existingTabNames);

      // Build the full dashboard structure with new tab
      const payload: DashboardTab = {
        ...existingTabs,
        [newTabName]: [widget] as MarketApps
      };

      addWidgetToDashboard({
        dashboardName,
        tabName: newTabName,
        widgetId: widget.id || '',
        payload
      });
      onClose();
    },
    [isPending, createWidget, addWidgetToDashboard, onClose, allDashboards]
  );

  const getDashboardTabs = useCallback(
    (dashboardName: string) => {
      const tabs = allDashboards?.[dashboardName];
      if (!tabs) return null;
      return Object.keys(tabs).map(name => ({ tabId: name, tabName: name }));
    },
    [allDashboards]
  );

  return (
    <DashboardTargetMenu
      anchorEl={anchorEl}
      dashboards={Object.keys(allDashboards || {})}
      disabled={isPending}
      getTabs={getDashboardTabs}
      open={open}
      onClose={onClose}
      onCreateDashboard={() => {
        handleCreateNewDashboard();
        return null;
      }}
      onCreateNewTab={handleDashboardName => {
        handleCreateNewTab(handleDashboardName);
      }}
      onSelectTab={(dashboardName, tab) => {
        handleAddToTab(dashboardName, tab.tabName);
      }}
    />
  );
};

export default AddToDashboardMenu;
